import { useMutation, useQuery } from "@apollo/client";
import { Toast } from "@aurem/ui";
import { AuthStatus, useAuth } from "providers/AuthProvider";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo } from "react";
import { gql } from "types";

type PreferredLanguageContextType = {
  preferredLanguage: string | null;
  loading: boolean;
  updatePreferredLanguage: (language: string) => Promise<void>;
};

const PreferredLanguageContext =
  createContext<PreferredLanguageContextType | null>(null);

const PREFERRED_LANGUAGE_QUERY = gql(/* GraphQL */ `
  query PreferredLanguage {
    user {
      id
      profile {
        id
        preferredLanguage
      }
    }
  }
`);

const UPDATE_PREFERRED_LANGUAGE_MUTATION = gql(/* GraphQL */ `
  mutation UpdatePreferredLanguage($language: String!) {
    updatePreferredLanguage(language: $language) {
      id
      preferredLanguage
    }
  }
`);

type Props = {
  children: ReactNode;
};

export const PreferredLanguageProvider = ({ children }: Props) => {
  const { authState } = useAuth();

  const { data, loading } = useQuery(PREFERRED_LANGUAGE_QUERY, {
    skip: authState.status !== AuthStatus.Authenticated,
  });

  const [updateLanguageMutation] = useMutation(
    UPDATE_PREFERRED_LANGUAGE_MUTATION,
  );

  const preferredLanguage = data?.user?.profile?.preferredLanguage ?? null;

  const updatePreferredLanguage = useCallback(
    async (language: string) => {
      try {
        await updateLanguageMutation({
          variables: { language },
        });
      } catch (error) {
        Toast.error({
          title: (error as Error).message,
        });
        throw error;
      }
    },
    [updateLanguageMutation],
  );

  const contextValue = useMemo<PreferredLanguageContextType>(() => {
    return {
      preferredLanguage,
      loading,
      updatePreferredLanguage,
    };
  }, [preferredLanguage, loading, updatePreferredLanguage]);

  return (
    <PreferredLanguageContext value={contextValue}>
      {children}
    </PreferredLanguageContext>
  );
};

export const usePreferredLanguage = (): PreferredLanguageContextType => {
  const ctx = useContext(PreferredLanguageContext);
  if (!ctx) {
    throw new Error(
      "usePreferredLanguage must be used within PreferredLanguageProvider",
    );
  }
  return ctx;
};
